import React, { useState } from "react";
import { parseDecimalIT } from "../../utils/parseDecimalIT";

const UNITA_SHORT = {
  MIN_DIE: "min/die",
  MIN_SETT: "min/sett",
  ORE_SETT: "h/sett",
};

const cellKey = (rowKey, colKey) => `${rowKey}|${colKey}`;

/**
 * Griglia editabile riga (profilo) × colonna (target: area o mansione) per
 * la matrice requisiti — riusata sia per la matrice normativa regionale che
 * per gli standard aziendali per-RSA (stesso shape di cella).
 *
 * Input controllati, salvataggio per singola cella al blur (o Invio) — niente
 * remount-key / staged-pending: era la causa del bug fixato in b48ced8 sulla
 * vecchia pagina.
 *
 * cells: { "rowKey|colKey": requisito } — requisito: {id, valore, unita,
 *   tipocalcolo, rapportobase, rapportovalore, ...} oppure assente (nessun
 *   requisito ancora salvato per quella cella).
 * Le righe con tipocalcolo "RAPPORTO" sono sola-lettura in griglia — si
 * editano tramite onEditRapporto (modal dedicato, fuori scope della griglia).
 */
export default function RequisitiMatrixEditor({
  rows,
  columns,
  cells,
  onSaveCell,
  onDeleteCell,
  onEditRapporto,
}) {
  const [editing, setEditing] = useState({});
  const [saving, setSaving] = useState({});
  const [errors, setErrors] = useState({});

  const commit = async (rowKey, colKey) => {
    const key = cellKey(rowKey, colKey);
    if (!(key in editing)) return;
    const existing = cells[key];
    const parsed = parseDecimalIT(editing[key]);

    if (Number.isNaN(parsed)) {
      setErrors((e) => ({ ...e, [key]: "Valore non numerico" }));
      return;
    }

    setSaving((s) => ({ ...s, [key]: true }));
    setErrors((e) => ({ ...e, [key]: null }));
    try {
      if (parsed == null) {
        if (existing) await onDeleteCell({ existing });
      } else {
        await onSaveCell({ rowKey, colKey, existing, valore: parsed });
      }
      setEditing((s) => {
        const next = { ...s };
        delete next[key];
        return next;
      });
    } catch (err) {
      setErrors((e) => ({
        ...e,
        [key]: err.message || "Errore di salvataggio",
      }));
    } finally {
      setSaving((s) => ({ ...s, [key]: false }));
    }
  };

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm border-collapse">
        <thead>
          <tr>
            <th className="sticky left-0 bg-white py-2 px-3 text-left text-xs text-gray-500 uppercase border-b border-gray-200">
              Profilo
            </th>
            {columns.map((c) => (
              <th
                key={c.key}
                className="py-2 px-3 text-xs text-gray-500 uppercase border-b border-gray-200 text-right whitespace-nowrap"
              >
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key} className="border-b border-gray-100">
              <td className="sticky left-0 bg-white py-2 px-3 font-medium text-gray-700 whitespace-nowrap">
                {r.label}
              </td>
              {columns.map((c) => {
                const key = cellKey(r.key, c.key);
                const existing = cells[key];
                const isRapporto = existing?.tipocalcolo === "RAPPORTO";

                if (isRapporto) {
                  return (
                    <td key={c.key} className="py-2 px-3 text-right">
                      <button
                        onClick={() => onEditRapporto?.(existing, r.key, c.key)}
                        className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded hover:bg-blue-100"
                        title="Requisito a rapporto — modifica"
                      >
                        {existing.rapportovalore}/{existing.rapportobase}
                      </button>
                    </td>
                  );
                }

                const value = editing[key] ?? existing?.valore ?? "";
                const isFlatSett = existing?.tipocalcolo === "FLAT_SETT";
                return (
                  <td key={c.key} className="py-1 px-2 text-right align-top">
                    <input
                      type="text"
                      inputMode="decimal"
                      value={value}
                      onChange={(e) =>
                        setEditing((s) => ({ ...s, [key]: e.target.value }))
                      }
                      onBlur={() => commit(r.key, c.key)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") e.target.blur();
                      }}
                      disabled={saving[key]}
                      title={existing?.note || undefined}
                      className={`w-20 text-right border rounded px-1.5 py-1 text-sm ${
                        errors[key]
                          ? "border-red-400"
                          : isFlatSett
                            ? "border-blue-300 bg-blue-50"
                            : "border-gray-300"
                      } ${saving[key] ? "bg-gray-50" : ""}`}
                    />
                    {existing?.unita && (
                      <span className="block text-[10px] text-gray-400">
                        {UNITA_SHORT[existing.unita] || existing.unita}
                        {isFlatSett && " fisso"}
                      </span>
                    )}
                    {existing?.note && (
                      <span
                        className="block text-[10px] text-gray-400"
                        title={existing.note}
                      >
                        ⓘ nota
                      </span>
                    )}
                    {errors[key] && (
                      <span className="block text-[10px] text-red-600">
                        {errors[key]}
                      </span>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
          {rows.length === 0 && (
            <tr>
              <td
                colSpan={columns.length + 1}
                className="py-6 text-center text-gray-400"
              >
                Nessun profilo configurato
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
